/**
 * Escrituras en el libro como órdenes (Command). Cada una es idempotente: localiza la fila por `id` (o por clave en
 * Settings) y la actualiza o la añade, así que reintentar es seguro. Solo se escriben las columnas del contrato; las
 * demás de la fila se conservan tal cual.
 */
import type { Exercise } from '@sut/domain';
import { boolText, safeText, str } from './cells.js';
import { headerIndex, type Session, type WingChunPractice } from './parse.js';
import { ROUTINE_HEADERS, SESSION_HEADERS, TABS, WINGCHUN_HEADERS, type Values } from './schema.js';

export type ValueInput = 'RAW' | 'USER_ENTERED';

/** Puerto mínimo sobre la API de Google Sheets (lo implementa el dispositivo con el token del usuario). */
export interface SheetsApi {
  get(range: string): Promise<Values>;
  update(data: Array<{ range: string; values: Values }>, input?: ValueInput): Promise<void>;
  append(range: string, values: Values, input?: ValueInput): Promise<{ updatedRange: string }>;
  addSheet(title: string): Promise<void>;
  clear(ranges: string[]): Promise<void>;
  deleteRows(rows: Array<{ tab: string; row: number }>): Promise<void>;
}

export type Command =
  | { type: 'upsert-session'; session: Session }
  | { type: 'delete-session'; id: string }
  | { type: 'upsert-wingchun'; practice: WingChunPractice }
  | { type: 'delete-wingchun'; id: string }
  | { type: 'set-setting'; key: string; value: string }
  /** Guarda la rutina entera (pocas filas; editarla es raro). Quitar un ejercicio = active false, nunca borrarlo. */
  | { type: 'replace-routine'; rows: Exercise[] };

export type CommandResult = { status: 'done' } | { status: 'conflict'; message: string };

/** Id nuevo, en el mismo formato que los de iOS (UUID en mayúsculas). */
export const newId = () => crypto.randomUUID().toUpperCase();

const colLetter = (n: number): string => {
  let s = '';
  for (let x = n; x > 0; x = Math.floor((x - 1) / 26)) s = String.fromCharCode(65 + ((x - 1) % 26)) + s;
  return s;
};

/** Un instante se guarda como texto (el apóstrofo evita que Sheets lo convierta y pierda la zona horaria). */
const instant = (iso: string) => (iso ? `'${iso}` : '');
const num = (n: number | null) => (n === null ? '' : n);

export function sessionCells(s: Session): Record<string, unknown> {
  return {
    id: s.id,
    date: s.date,
    floorCompletedAt: instant(s.floorCompletedAt),
    normalStartedAt: instant(s.normalStartedAt),
    normalFinishedAt: instant(s.normalFinishedAt),
    sessionType: s.sessionType,
    rounds: s.rounds,
    durationSeconds: s.durationSeconds,
    effort: num(s.effort),
    activity: safeText(s.activity),
    pain: boolText(s.pain),
    painNote: safeText(s.painNote),
    note: safeText(s.note),
    nextDaySoreness: num(s.nextDaySoreness),
    wantToReturn: boolText(s.wantToReturn),
    recoveryLoggedAt: instant(s.recoveryLoggedAt),
    exerciseFeedback: s.exerciseFeedback,
    routineSnapshot: s.routineSnapshot,
    warmupSeconds: num(s.warmupSeconds),
  };
}

export function routineCells(e: Exercise): unknown[] {
  return [e.id, e.section, e.order, safeText(e.name), e.measure, e.target, e.question, safeText(e.prompt), boolText(e.active)];
}

export function wingChunCells(p: WingChunPractice): Record<string, unknown> {
  return {
    id: p.id,
    date: p.date,
    form: p.form,
    startedAt: instant(p.startedAt),
    completedAt: instant(p.completedAt),
    durationSeconds: num(p.durationSeconds),
    isDailyMinimum: boolText(p.isDailyMinimum),
    source: p.source,
    outcome: p.outcome,
    note: safeText(p.note),
    createdAt: instant(p.createdAt),
  };
}

const findRow = (values: Values, col: number, value: string) => values.findIndex((r, i) => i > 0 && str(r[col]) === value);

/**
 * Columnas del contrato que aún no existen en la pestaña: se añaden AL FINAL de la fila de cabeceras (igual que hacía
 * Apps Script con `exerciseFeedback`), sin mover ni renombrar ninguna existente.
 */
async function ensureColumns(api: SheetsApi, tab: string, values: Values, names: readonly string[]): Promise<void> {
  const header = values[0];
  if (!header?.length) return;
  const idx = headerIndex(values);
  const missingCols = names.filter(n => !idx.has(n));
  if (!missingCols.length) return;
  await api.update([{ range: `${tab}!${colLetter(header.length + 1)}1`, values: [missingCols] }], 'RAW');
  header.push(...missingCols);
}

/** Actualiza la fila con ese id o la añade al final. */
async function upsertById(api: SheetsApi, tab: string, id: string, cells: Record<string, unknown>, columns: readonly string[] = []): Promise<CommandResult> {
  if (!id) return { status: 'conflict', message: 'Registro sin id: no se puede guardar' };
  const values = await api.get(`${tab}!A1:Z`);
  const header = values[0] ?? [];
  if (!header.length) return { status: 'conflict', message: `La pestaña ${tab} no tiene cabeceras` };
  await ensureColumns(api, tab, values, columns);
  const idx = headerIndex(values);
  const at = findRow(values, idx.get('id') ?? 0, id);
  const row: unknown[] = Array.from({ length: header.length }, (_, i) => (at > 0 ? values[at]?.[i] ?? '' : ''));
  for (const [name, v] of Object.entries(cells)) {
    const i = idx.get(name);
    if (i !== undefined) row[i] = v;
  }
  if (at > 0) {
    await api.update([{ range: `${tab}!A${at + 1}:${colLetter(header.length)}${at + 1}`, values: [row] }], 'USER_ENTERED');
  } else {
    await api.append(`${tab}!A1`, [row], 'USER_ENTERED');
  }
  return { status: 'done' };
}

async function deleteById(api: SheetsApi, tab: string, id: string): Promise<CommandResult> {
  const values = await api.get(`${tab}!A1:Z`);
  const at = findRow(values, headerIndex(values).get('id') ?? 0, id);
  if (at > 0) await api.deleteRows([{ tab, row: at + 1 }]);
  return { status: 'done' };
}

/** Pestañas que la app crea si faltan (Sync.gs creaba WingChun al vuelo; Rutina es nueva), con sus cabeceras. */
async function ensureTab(api: SheetsApi, tab: string, headers: readonly string[]): Promise<void> {
  let header: Values;
  try { header = await api.get(`${tab}!A1:Z1`); }
  catch { await api.addSheet(tab); header = []; }
  if (!(header[0] ?? []).length) {
    await api.update([{ range: `${tab}!A1`, values: [[...headers]] }], 'RAW');
  }
}

export async function executeCommand(api: SheetsApi, cmd: Command): Promise<CommandResult> {
  switch (cmd.type) {
    case 'upsert-session':
      return upsertById(api, TABS.sessions, cmd.session.id, sessionCells(cmd.session), SESSION_HEADERS);
    case 'delete-session':
      return deleteById(api, TABS.sessions, cmd.id);
    case 'upsert-wingchun':
      await ensureTab(api, TABS.wingChun, WINGCHUN_HEADERS);
      return upsertById(api, TABS.wingChun, cmd.practice.id, wingChunCells(cmd.practice));
    case 'delete-wingchun':
      return deleteById(api, TABS.wingChun, cmd.id);
    case 'set-setting': {
      const values = await api.get(`${TABS.settings}!A1:C`);
      const at = findRow(values, 0, cmd.key);
      if (at > 0) await api.update([{ range: `${TABS.settings}!B${at + 1}`, values: [[safeText(cmd.value)]] }], 'USER_ENTERED');
      else await api.append(`${TABS.settings}!A1`, [[cmd.key, safeText(cmd.value), '']], 'USER_ENTERED');
      return { status: 'done' };
    }
    case 'replace-routine': {
      await ensureTab(api, TABS.routine, ROUTINE_HEADERS);
      await api.clear([`${TABS.routine}!A2:Z`]);
      if (cmd.rows.length) await api.update([{ range: `${TABS.routine}!A2`, values: cmd.rows.map(routineCells) }], 'USER_ENTERED');
      return { status: 'done' };
    }
  }
}
